<template>
  <div id="login-page">
    <div v-if="showToast" :class="['toast', toastType]">
      {{ toastMessage }}
    </div>
    <div v-if="loadingSSO" class="loading-overlay">
      <p class="loading-text">Entrando...</p>
    </div>

    <div class="login-container">
      <div class="yw-logoContainer">
        <img class="yw-loginpage-logo" title="Logotipo" src="/Logo_Magnum.png" />
      </div>

      <!-- 🔐 Mensagem de sessão ativa -->
      <div v-if="checkingSession" class="redirect-message">
        <p>🔐 Sessão ativa, redirecionando...</p>
      </div>

      <!-- 🔁 Formulário -->
      <form v-else id="loginForm" @submit.prevent="entrarViaFormulario">
        <div class="login-grid">
          <div class="form-group">
            <label for="email">E-mail:</label>
            <input v-model="email" type="email" id="email" />
          </div>

          <div class="form-group">
            <label for="password">Senha:</label>
            <input v-model="password" type="password" id="password" />
          </div>

          <div class="form-group">
            <label>
              <input type="checkbox" v-model="acessoComWhatsapp" />
              Acesso com WhatsApp
            </label>
          </div>

          <button :disabled="loadingSSO" type="submit">
            {{ loadingSSO ? "Iniciando..." : "Entrar" }}
          </button>

          <!-- 🔘 Botão com estilo Google (usa SAML por baixo) -->
          <button type="button" class="google-button" @click.prevent="entrarComGoogle" :disabled="loadingSSO">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google"
              class="google-icon" />
            {{ loadingSSO ? "Entrando com Google..." : "Entrar com Google" }}
          </button>

          <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { auth } from "@/firebase";
import { getRedirectResult, signInWithEmailAndPassword, signOut } from "firebase/auth";

const FIREBASE_SSO_ENTRY =
  "https://production-servers-magnumtires.firebaseapp.com/mag-auth.html";

const apiBaseUrl =
  process.env.VUE_APP_API_BASE_URL || "https://177.36.4.221:3000";

const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

// 🌐 Domínios corporativos (SSO)
const CORPORATE_DOMAINS = [
  "magnumtires.com.br",
  "repcomercialmagnumtires.com.br",
  "prontopneus.com.br",
];

function getDomain(email) {
  const parts = (email || "").toLowerCase().trim().split("@");
  return parts.length === 2 ? parts[1] : "";
}

function isCorporateEmail(email) {
  return CORPORATE_DOMAINS.includes(getDomain(email));
}

export default {
  setup() {
    const router = useRouter();

    const email = ref("");
    const password = ref("");
    const acessoComWhatsapp = ref(false);
    const loadingSSO = ref(false);
    const errorMessage = ref("");
    const checkingSession = ref(true);

    const showToast = ref(false);
    const toastMessage = ref("");
    const toastType = ref("error");

    function persistPreAuthInfo(emailValue) {
      localStorage.setItem(
        "acessoComWhatsapp",
        acessoComWhatsapp.value ? "true" : "false"
      );

      if (emailValue) {
        localStorage.setItem("email_ini", emailValue);
        localStorage.setItem("ssoEmail", emailValue);
      }
    }

    function triggerToast(message, type = "error") {
      toastMessage.value = message;
      toastType.value = type;
      showToast.value = true;

      setTimeout(() => {
        showToast.value = false;
      }, 3000);
    }

    async function afterAuthCommonFlow({ user, token }) {
      // ✅ Seta props globais (se você usa isso no app)
      const app = window.__app__;
      if (app) {
        app.config.globalProperties.$isAuthenticated = true;
        app.config.globalProperties.$userEmail = user.email;
      }

      // ✅ Storage básico
      localStorage.setItem("email_ini", user.email || "");
      localStorage.setItem("ssoEmail", user.email || "");
      localStorage.setItem("userToken", token);

      // ✅ Registra sessão no backend (mantém seu fluxo atual)
      try {
        await fetch(`${apiBaseUrl}/store-session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: user.email }),
        });
      } catch (e) {
        console.warn("[AUTH] Erro ao chamar /store-session:", e);
      }

      const acessoComWhatsappAtivo =
        localStorage.getItem("acessoComWhatsapp") === "true";

      // Se NÃO for acesso com WhatsApp → vai direto
      if (!acessoComWhatsappAtivo) {
        router.replace("/pesquisa-cnpj");
        return;
      }

      // Se for acesso com WhatsApp → decide se precisa /qr-code
      try {
        const res = await fetch(`${apiBaseUrl}/qr-code-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = await res.json().catch(() => ({}));
        const scanned = !!body?.scanned;

        let lastPage = localStorage.getItem("currentPage");
        if (!lastPage || lastPage === "/" || lastPage === "/login") {
          lastPage = "/pesquisa-cnpj";
        }

        router.replace(scanned ? lastPage : "/qr-code");
      } catch (e) {
        console.error("[AUTH] Erro ao verificar QR:", e);
        router.replace("/qr-code");
      }
    }

    // Botão "Entrar com Google" → sempre usa SSO (SAML)
    const entrarComGoogle = () => {
      try {
        loadingSSO.value = true;
        errorMessage.value = "";

        const trimmedEmail = email.value.trim();
        persistPreAuthInfo(trimmedEmail);

        const redirectAfter = `${window.location.origin}/pesquisa-cnpj`;
        const url = new URL(FIREBASE_SSO_ENTRY);
        url.searchParams.set("redirect", redirectAfter);

        url.searchParams.set(
          "acessoWhatsapp",
          acessoComWhatsapp.value ? "true" : "false"
        );

        if (trimmedEmail) {
          url.searchParams.set("email", encodeURIComponent(trimmedEmail));
        }

        window.location.href = url.toString();
      } catch (e) {
        console.error("[SSO] Falha ao redirecionar para mag-auth:", e);
        errorMessage.value = e?.message || "Falha ao iniciar o login.";
        loadingSSO.value = false;
      }
    };

    const loginViaEmailSenha = async (trimmedEmail) => {
      // ✅ sempre exige senha para email/senha (prod e local)
      if (!password.value) {
        errorMessage.value = "Informe a senha.";
        return;
      }

      try {
        loadingSSO.value = true;
        errorMessage.value = "";

        const cred = await signInWithEmailAndPassword(
          auth,
          trimmedEmail,
          password.value
        );

        const user = cred.user;
        const token = await user.getIdToken(true);

        // ✅ Aqui NÃO existe allowlist externa.
        // A “allowlist” é: se o usuário existe no Firebase Authentication, ele entra.
        persistPreAuthInfo(trimmedEmail);
        await afterAuthCommonFlow({ user, token });
      } catch (e) {
        console.error("[AUTH] Erro no login email/senha:", e);

        let msg = "Credenciais inválidas.";

        triggerToast(msg, "error");
      } finally {
        loadingSSO.value = false;
      }
    };

    const entrarViaFormulario = async () => {
      const trimmedEmail = email.value.trim();

      if (!trimmedEmail) {
        errorMessage.value = "Informe um e-mail para entrar.";
        return;
      }

      const corporate = isCorporateEmail(trimmedEmail);

      // ✅ NÃO corporativo → email/senha (prod e local)
      if (!corporate) {
        await loginViaEmailSenha(trimmedEmail);
        return;
      }

      // ✅ corporativo:
      // - Localhost: pode usar email/senha (útil em DEV)
      // - Produção: manda para SSO
      if (isLocalhost) {
        await loginViaEmailSenha(trimmedEmail);
        return;
      }

      // PRODUÇÃO (corporativo): SSO via mag-auth
      try {
        loadingSSO.value = true;
        errorMessage.value = "";

        persistPreAuthInfo(trimmedEmail);

        const redirectAfter = `${window.location.origin}/pesquisa-cnpj`;
        const url = new URL(FIREBASE_SSO_ENTRY);
        url.searchParams.set("redirect", redirectAfter);

        if (trimmedEmail) {
          url.searchParams.set("email", encodeURIComponent(trimmedEmail));
        }

        window.location.href = url.toString();
      } catch (e) {
        console.error("[Form→SSO] Falha ao redirecionar para mag-auth:", e);
        errorMessage.value = e?.message || "Falha ao iniciar o login.";
        loadingSSO.value = false;
      }
    };

    const resolverRetornoSSO = async () => {
      try {
        const result = await getRedirectResult(auth);
        sessionStorage.removeItem("ssoRedirectInFlight");

        if (result?.user) {
          const user = result.user;

          if (user?.email) {
            localStorage.setItem("email_ini", user.email);
            localStorage.setItem("ssoEmail", user.email);
          }

          const token = await user.getIdToken(true);

          // ✅ redundância: SSO só para corporativo
          if (user?.email && !isCorporateEmail(user.email)) {
            try {
              await signOut(auth);
            } catch {
              localStorage.removeItem("userToken");
              errorMessage.value =
                "Acesso via SSO permitido apenas para e-mails corporativos.";
              return;
            }
          }

          await afterAuthCommonFlow({ user, token });
        } else {
          console.debug("[SSO] getRedirectResult retornou null.");
        }
      } catch (e) {
        sessionStorage.removeItem("ssoRedirectInFlight");
        console.error("[SSO] Erro em getRedirectResult:", e);
        errorMessage.value =
          e?.message || "Falha ao completar o login via SSO.";
      } finally {
        checkingSession.value = false;
        loadingSSO.value = false;
      }
    };

    onMounted(async () => {
      await resolverRetornoSSO();
    });

    return {
      email,
      password,
      acessoComWhatsapp,
      loadingSSO,
      errorMessage,
      checkingSession,
      showToast,
      toastMessage,
      toastType,
      entrarViaFormulario,
      entrarComGoogle,
    };
  },
};
</script>

<style src="@/assets/styleLoginPage.css"></style>
